import { computed, inject, onBeforeMount, provide, reactive, ref } from 'vue';
import EventSessionsColumnComponent
  from '@/components/events/blocks/event-sessions-column/event-sessions-column.vue';
import SkeletonLoader from '@/components/content/views/skeleton-loader/skeleton-loader.vue';
import TabsComponent from '@/components/_shared/_partials/tabs/tabs.vue';
import TabComponent from '@/components/_shared/_partials/tab/tab.vue';

/**
 * Event Schedule Tabs View
 * @module Events Schedule Tabs View
 *
 * @prop { Config } config Component configuration
 * @prop { TabsConfig } {tabsConfig} Tabs configuration object
 *
 */

export default {
  name: 'EventScheduleTabsView',
  props: {
    config: {
      type: Object,
      required: true,
      default: {},
    },
    tabsConfig: {
      type: Object,
      required: true,
      default: {},
    },

  },
  components: { EventSessionsColumnComponent, SkeletonLoader, TabsComponent, TabComponent },
  setup(props) {

    let config = inject('config');
    config = { ...config, ...props.config };
    provide('config', config);

    const eventProvider = inject('eventProvider');

    onBeforeMount(async () => {
      await loadEvent();
    });

    const state = reactive({
      activeTab: ref(null),
      event: ref(null),
      hasEvent: ref(false),
      defaultTabColor: '#31373E',
      tabsRef: 'tabs',
      tabs: [],
      isReplay: props.tabsConfig.hasOwnProperty('isReplay') ? props.tabsConfig.isReplay : false,
      showPDF: props.tabsConfig.hasOwnProperty('showPDF') ? props.tabsConfig.showPDF : false,
      defaultTab: props.tabsConfig.hasOwnProperty('defaultTab') ? props.tabsConfig.defaultTab : false,
      downloadPDF: computed(() => {
        if (state.hasEvent) {
          if (state.event.schedule_pdf_url) {
            return state.event.schedule_pdf_url;
          }
        }
        return false;
      }),
      defaultSkeleton: [[{ track: true }, { track: true }],[{ track: true }, { track: true }]],
    });

    const calculateActiveTab = () => {
      if (state.defaultTab) {
        for (let i = 0; i < state.tabs.length; i++) {
          if (state.defaultTab === state.tabs[i].id) {
            state.activeTab = state.defaultTab;
            return state.activeTab;
          }
        }
      }
      const now = new Date();
      for (let i = 0; i < state.tabs.length; i++) {
        if (state.tabs[i].date) {
          const tabDate = new Date(state.tabs[i].date);
          if (tabDate.getMonth() === now.getMonth() && tabDate.getDate() === now.getDate()) {
            state.activeTab = state.tabs[i].id;
            return state.activeTab;
          }
        }
      }

      state.activeTab = state.tabs[0].id;

      return state.activeTab;
    };

    const calculateTrackColor = (id, color) => {
      if (color) {
        return color;
      }
      if (state.hasEvent) {
        let track = getTrack(state.event.tracks, id);
        if (track) {
          return track.custom_class ?? state.defaultTabColor;
        }
      }

      return state.defaultTabColor;
    };

    const calculateTrackSessions = (id, day, include, exclude) => {
      if (state.hasEvent) {
        let track = getTrack(state.event.tracks, id);
        if (track) {
          let sessions = track.sessions;
          if (day) {
            sessions = sessions.filter(filterSessionByStartDay(day));
          }
          if (include && include.length) {
            sessions = sessions.filter(filterSessionByInclude(include));
          } else if (exclude && exclude.length) {
            sessions = sessions.filter(filterSessionByExclude(exclude));
          }
          if (state.isReplay) {
            sessions = sessions.filter(filterSessionByReplay());
          }

          return sessions;
        }
      }

      return null;
    };

    const calculateTrackTitle = (id, title) => {
      if (title && '' !== title) {
        return title;
      }
      if (state.hasEvent) {
        let track = getTrack(state.event.tracks, id);
        if (track) {
          return track.name;
        }
      }

      return null;
    };

    const getTrack = (tracks, id) => {
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].id == id) {

          return tracks[i];
        }
      }

      return false;
    };

    const filterSessionByStartDay = (startDay) => {
      return function(session) {
        let date = session.starts_at;
        let dayValue = date.slice(8, 10);

        return parseInt(dayValue, 10) === parseInt(startDay, 10);
      };
    };

    const filterSessionByInclude = (includeArray) => {
      return function(session) {
        let id = session.id;

        return includeArray.includes(id);

      };
    };

    const filterSessionByExclude = (excludeArray) => {
      return function(session) {
        let id = session.id;

        return !excludeArray.includes(id);

      };
    };

    const filterSessionByReplay = () => {
      return function(session) {

        return session.video_key && session.video_key !== '';

      };
    };

    const loadEvent = async () => {
      let response = await eventProvider().loadEvent(props.tabsConfig.event, config);
      if (response.status === 'error' || response.success === false) {
        if (config.debug) {
          console.log('loadEvent error', props.tabsConfig.event, response.message);
        }
      } else {
        state.event = response.data.data;
        state.tabs = props.tabsConfig.tabs ? props.tabsConfig.tabs : state.event.schedule.main;
        calculateActiveTab();
        state.hasEvent = true;
      }

      return response;
    };

    return {
      state,
      loadEvent,
      calculateTrackColor,
      calculateTrackSessions,
      calculateTrackTitle,
      calculateActiveTab
    };
  },
  methods: {
    tabChanged: async function(e) {
      this.state.activeTab = e.tab.hash;
    },
    tabClicked: async function(id) {
      this.$refs[this.state.tabsRef].selectTab(id);
      this.state.activeTab = id;
    },
  },

};
